import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import EventCard from '../Components/EventCard';
import Loading from '../Components/Loading';
import { useCachedGet } from '../Services/useCachedGet';
import { useReveal } from '../Components/Fx/useReveal';
import TrumpFeature from '../Components/TrumpFeature';

const HomePage = () => {
   const { data: events, loading } = useCachedGet<any[]>(`${process.env.REACT_APP_API}/events`);
   const [drafts, setDrafts] = useState<any[]>([]);
   const [activeTag, setActiveTag] = useState<string | null>(null);
   const scope = useRef<HTMLDivElement>(null);

   useEffect(() => {
      if (process.env.NODE_ENV !== 'development') return;
      axios
         .get(`${process.env.REACT_APP_API}/drafts`)
         .then(result => result.data && setDrafts(result.data))
         .catch(err => console.log(err));
   }, []);

   const tags = useMemo(() => {
      const set = new Map<string, string>();
      (events || []).forEach(e =>
         e.tags?.forEach((t: any) => t?.label && set.set(t.label, t.label))
      );
      return Array.from(set.keys()).slice(0, 12);
   }, [events]);

   const visible = useMemo(
      () =>
         (events || []).filter(e => !activeTag || e.tags?.some((t: any) => t?.label === activeTag)),
      [events, activeTag]
   );

   useReveal(scope, [visible, drafts]);

   if (loading) return <Loading />;

   const [featured, ...rest] = visible;

   return (
      <>
         <TrumpFeature />
         <div ref={scope} className="page">
            <div
               className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
               data-reveal
            >
               <div>
                  <p className="eyebrow mb-3">Index</p>
                  <p className="whitespace-nowrap font-display text-4xl text-bone">
                     {String(visible.length).padStart(2, '0')}{' '}
                     <span className="text-bone-dim">open files</span>
                  </p>
               </div>
               {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                     <button
                        type="button"
                        onClick={() => setActiveTag(null)}
                        className={activeTag === null ? 'chip-signal' : 'chip hover:text-bone'}
                     >
                        All
                     </button>
                     {tags.map(tag => (
                        <button
                           type="button"
                           key={tag}
                           onClick={() => setActiveTag(tag === activeTag ? null : tag)}
                           className={activeTag === tag ? 'chip-signal' : 'chip hover:text-bone'}
                        >
                           {tag}
                        </button>
                     ))}
                  </div>
               )}
            </div>

            {featured && (
               <Link to={`/event/${featured.id}`} className="mb-6 block" data-reveal>
                  <EventCard event={featured} index={0} featured />
               </Link>
            )}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
               {rest.map((event, i) => (
                  <Link to={`/event/${event.id}`} key={event.id} className="block" data-reveal>
                     <EventCard event={event} index={i + 1} />
                  </Link>
               ))}
            </div>

            {!visible.length && (
               <p className="py-20 text-center text-bone-muted">No cases match this filter.</p>
            )}

            {process.env.NODE_ENV === 'development' && drafts.length > 0 && (
               <section className="mt-24">
                  <div className="rule mb-10" />
                  <p className="eyebrow mb-3 text-signal-soft">Dev only</p>
                  <h2 className="headline">Drafts</h2>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                     {drafts.map((event, i) => (
                        <Link
                           to={`/event/${event.id}`}
                           key={event.id}
                           className="block"
                           data-reveal
                        >
                           <EventCard event={event} index={i} />
                        </Link>
                     ))}
                  </div>
               </section>
            )}
         </div>
      </>
   );
};

export default HomePage;
